import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { extraordinaryColors } from "../../utils/theme";
import { EmployeeSpendTooltipRenderer } from "./EmployeeSpendTooltip/EmployeeSpendTooltip";

const areaChartOptions = {
  chart: {
    height: 355,
    type: "area",
    toolbar: {
      show: false
    }
  },
  fill: {
    type: "gradient",
    gradient: {
      shadeIntensity: 1,
      type: "vertical",
      inverseColors: true,
      gradientToColors: [extraordinaryColors.purple.main],
      opacityFrom: 0.6,
      opacityTo: 0
    }
  },
  dataLabels: {
    enabled: false
  },
  stroke: {
    curve: "smooth",
    width: 6,
    lineCap: "round"
  },
  markers: {
    colors: "#A46FE0",
    strokeColor: "#fff"
  },
  grid: {
    show: false,
    xaxis: {
      lines: {
        show: false
      },
      ticks: {
        show: false
      },
      labels: {
        formatter: function (val) {
          return val.toFixed(0);
        }
      }
    }
  },
  yaxis: {
    labels: {
      show: false
    }
  },
  xaxis: {
    crosshairs: {
      stroke: {
        color: "#000000",
        dashArray: 0,
        width: 2
      }
    }
  }
};

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const generateXAxis = (slot, monthlyStats, dailyStats) => {
  if (slot === "12m" && monthlyStats) {
    return {
      categories: monthlyStats.map(stat => months[stat.month - 1]),
      tickAmount: 12
    };
  }

  if (slot === "1m" && dailyStats) {
    return {
      categories: dailyStats.map(stat => new Date(stat.date).getDate()),
      tickAmount: dailyStats.length - 1
    };
  }

  return { categories: [], tickAmount: 0 };
};

const EmployeeSpendChart = ({ slot, monthlyStats, dailyStats, loading }) => {
  const theme = useTheme();
  const { primary, secondary } = theme.palette.text;
  const line = theme.palette.divider;

  const [options, setOptions] = useState(areaChartOptions);
  const [series, setSeries] = useState([]);

  const seriesData = useMemo(() => {
    if (loading || !monthlyStats || !dailyStats) return { isLoading: true };

    return {
      "12m": {
        data: monthlyStats.map(stat => stat.totalAmount),
        volume: monthlyStats.map(stat => stat.transactionCount)
      },
      "1m": {
        data: dailyStats.map(stat => stat.totalAmount),
        volume: dailyStats.map(stat => stat.transactionCount)
      }
    };
  }, [monthlyStats, dailyStats, loading]);

  const RenderTooltip = useCallback(
    ({ series, seriesIndex, dataPointIndex, w }) => {
      return EmployeeSpendTooltipRenderer(
        { series, seriesIndex, dataPointIndex, w },
        seriesData[slot].volume
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, seriesData, slot]
  );

  useEffect(() => {
    setOptions((prevState) => {
      const xAxisSpec = generateXAxis(slot, monthlyStats, dailyStats);

      return {
        ...prevState,
        colors: [extraordinaryColors.purple.main],
        xaxis: {
          ...prevState.xaxis,
          categories: xAxisSpec.categories,
          labels: {
            style: {
              colors: [
                secondary,
                secondary,
                secondary,
                secondary,
                secondary,
                secondary,
                secondary,
                secondary,
                secondary,
                secondary,
                secondary,
                secondary
              ]
            }
          },
          tickAmount: xAxisSpec.tickAmount,
          tooltip: {
            enabled: false
          }
        },
        stroke: {
          colors: [theme.palette.primary.main]
        },
        grid: {
          borderColor: line
        },
        tooltip: {
          custom: RenderTooltip
        },
        theme: {
          mode: "light"
        }
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, secondary, line, theme, slot, seriesData]);

  useEffect(() => {
    if (seriesData.isLoading) return;
    setSeries([
      {
        name: "Amount",
        data: seriesData[slot].data
      }
    ]);
  }, [slot, seriesData]);

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="area"
      height={355}
    />
  );
};

EmployeeSpendChart.propTypes = {
  slot: PropTypes.string,
  monthlyStats: PropTypes.arrayOf(PropTypes.shape({
    totalAmount: PropTypes.number,
    transactionCount: PropTypes.number,
    month: PropTypes.number,
    year: PropTypes.number
  })),
  dailyStats: PropTypes.arrayOf(PropTypes.shape({
    totalAmount: PropTypes.number,
    transactionCount: PropTypes.number,
    date: PropTypes.string
  })),
  loading: PropTypes.bool
};

export default EmployeeSpendChart;
